/**
 * Phase 30 — task-milestone assignment mutation with optimistic update.
 *
 * Mirrors useTaskMutation.ts shape (onMutate cancel + setQueryData optimistic;
 * onError rollback from context; onSettled invalidate). Per D-12 we only
 * invalidate ['board', projectId, audience]; ['milestones', projectId] is
 * untouched because the milestone metadata didn't change.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { patchTaskMilestone, type BoardResponse } from '../services/api/board';
import type { BoardAudience } from './useBoard';

export interface AssignTaskMilestoneVars {
  taskId: number;
  milestoneId: number | null;
  projectId: string;
  audience: BoardAudience;
}

interface MutationContext {
  previous: BoardResponse | undefined;
}

export function useAssignTaskMilestone() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, AssignTaskMilestoneVars, MutationContext>({
    mutationFn: (input) => patchTaskMilestone(input.taskId, input.milestoneId),
    onMutate: async (input) => {
      const key = ['board', input.projectId, input.audience] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardResponse>(key);
      if (previous) {
        const optimistic = patchMilestoneInBoard(previous, input.taskId, input.milestoneId);
        queryClient.setQueryData<BoardResponse>(key, optimistic);
      }
      return { previous };
    },
    onError: (_err, vars, context) => {
      // Restore the snapshot taken in onMutate; toast surfacing is the caller's
      // responsibility via per-mutate { onError } option.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          ['board', vars.projectId, vars.audience],
          context.previous,
        );
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({
        queryKey: ['board', input.projectId, input.audience],
      });
    },
  });
}

// Pure helper — keeps the optimistic update logic testable in isolation.
// Mirrors patchVisibilityInBoard in useTaskMutation.ts (existing precedent).
export function patchMilestoneInBoard(
  board: BoardResponse,
  taskId: number,
  milestoneId: number | null,
): BoardResponse {
  return {
    ...board,
    columns: board.columns.map((col) => ({
      ...col,
      tasks: col.tasks.map((t) => (t.id === taskId ? { ...t, milestoneId } : t)),
    })),
  };
}
